<script setup lang="ts">
import { ref } from 'vue';
import Popover from 'primevue/popover';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { EntityMonitoringView } from '@/composables/useEntityMonitoring';

const props = defineProps<{
  monitoring: EntityMonitoringView;
}>();

// Popover (not an inline dropdown) because the banners clip overflow to mask their
// decorative SVG; the overlay escapes that clip and positions itself.
const panel = ref<InstanceType<typeof Popover> | null>(null);
const showPanel = (event: MouseEvent) => panel.value?.show(event);
const hidePanel = () => panel.value?.hide();
</script>

<template>
  <div v-if="props.monitoring.isMonitored" class="inline-flex">
    <!-- Icon-only chip: signals active monitoring -->
    <div
      class="flex h-9 w-9 cursor-help items-center justify-center rounded-full border border-amber-300 bg-amber-400 text-amber-950 shadow-sm ring-2 ring-white/25"
      role="img"
      aria-label="Entidade sob monitoramento ativo"
      @mouseenter="showPanel"
      @mouseleave="hidePanel"
    >
      <BaseIcon size="sm">monitor_heart</BaseIcon>
    </div>

    <Popover ref="panel">
      <div class="w-60 text-left">
        <div class="flex items-center gap-1.5">
          <BaseIcon size="sm" class="text-primary-600">monitor_heart</BaseIcon>
          <span class="text-sm font-semibold text-surface-900">Monitoramento ativo</span>
        </div>
        <p class="mt-1 text-xs leading-relaxed text-surface-500">
          Esta entidade está sob monitoramento contínuo de mudanças.
        </p>

        <div v-if="props.monitoring.policies.length" class="mt-2.5 border-t border-surface-100 pt-2.5">
          <div class="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-surface-400">
            {{ props.monitoring.policies.length === 1 ? 'Política' : 'Políticas' }}
            ({{ props.monitoring.policies.length }})
          </div>
          <ul class="flex flex-col gap-1.5">
            <li
              v-for="policy in props.monitoring.policies"
              :key="policy.policy_id"
              class="flex items-start gap-1.5 text-xs text-surface-700"
            >
              <BaseIcon size="sm" class="mt-0.5 shrink-0 text-emerald-500">check_circle</BaseIcon>
              <span class="min-w-0 flex-1 break-words">{{ policy.policy_name }}</span>
            </li>
          </ul>
        </div>
      </div>
    </Popover>
  </div>
</template>
